import { db } from '../config/firebase';
import {
  collection, doc, getDocs, getDoc, addDoc, updateDoc,
  query, where, orderBy, onSnapshot, Unsubscribe, Timestamp, deleteDoc
} from 'firebase/firestore';
import { HRDocumentType } from '../types/roles';

export interface HRDocument {
  id: string;
  employeeId: string;      // team_members doc ID or authUid
  employeeName: string;
  documentType: HRDocumentType;
  formName: string;
  status: 'pending' | 'submitted' | 'approved' | 'rejected' | 'expired';
  assignedDate: string;
  assignedBy: string;       // provider UID who assigned
  practiceOwnerId: string;  // scoping to practice
  dueDate?: string;
  submittedDate?: string;
  approvedDate?: string;
  approvedBy?: string;
  fileUrl?: string;         // Firebase Storage URL
  storagePath?: string;     // Firebase Storage path for deletion
  expirationDate?: string;
  notes?: string;
  priority: 'low' | 'medium' | 'high';
  liveContent?: string;     // Rich text HTML content for live-editable documents
  lastEditedAt?: string;
  lastEditedBy?: string;
  templateId?: string;       // Links to installed form_templates doc for fillable forms
  formResponses?: Record<string, any>; // Filled form field responses
  signedAt?: string;         // When employee signed/acknowledged
  signedByName?: string;     // Name used for signature
}

export interface HRFormTemplate {
  id: string;
  formName: string;
  documentType: HRDocumentType;
  description: string;
  required: boolean;
  hasExpiration: boolean;
  defaultExpirationMonths?: number;
}

// Default HR form templates (static — not stored in Firestore)
export const DEFAULT_HR_FORM_TEMPLATES: HRFormTemplate[] = [
  {
    id: 'i9-employment-verification',
    formName: 'I-9 Employment Eligibility Verification',
    documentType: 'i9',
    description: 'Federal form verifying identity and employment authorization',
    required: true,
    hasExpiration: false
  },
  {
    id: 'w4-tax-withholding',
    formName: 'W-4 Federal Tax Withholding',
    documentType: 'w4',
    description: 'Employee withholding certificate for federal income tax',
    required: true,
    hasExpiration: false
  },
  {
    id: 'direct-deposit',
    formName: 'Direct Deposit Authorization',
    documentType: 'direct_deposit',
    description: 'Bank account information for payroll direct deposit',
    required: true,
    hasExpiration: false
  },
  {
    id: 'background-check-authorization',
    formName: 'Background Check Authorization',
    documentType: 'background_check',
    description: 'Authorization to conduct criminal and employment background check',
    required: true,
    hasExpiration: false
  },
  {
    id: 'emergency-contact',
    formName: 'Emergency Contact Information',
    documentType: 'emergency_contact',
    description: 'Emergency contact details for employee',
    required: true,
    hasExpiration: false
  },
  {
    id: 'employee-handbook-acknowledgment',
    formName: 'Employee Handbook Acknowledgment',
    documentType: 'handbook_ack',
    description: 'Acknowledgment of receipt and understanding of employee handbook',
    required: true,
    hasExpiration: false
  },
  {
    id: 'employment-contract',
    formName: 'Employment Contract',
    documentType: 'employment_contract',
    description: 'Signed employment agreement',
    required: true,
    hasExpiration: false
  },
  {
    id: 'nda',
    formName: 'Non-Disclosure Agreement (NDA)',
    documentType: 'nda',
    description: 'Confidentiality and non-disclosure agreement',
    required: true,
    hasExpiration: false
  },
  {
    id: 'hipaa-training-certificate',
    formName: 'HIPAA Training Certificate',
    documentType: 'hipaa_training',
    description: 'Proof of HIPAA privacy and security training completion',
    required: true,
    hasExpiration: true,
    defaultExpirationMonths: 12
  },
  {
    id: 'professional-license',
    formName: 'Professional License Verification',
    documentType: 'license',
    description: 'Current professional license (therapist, counselor, psychologist, etc.)',
    required: false,
    hasExpiration: true,
    defaultExpirationMonths: 24
  },
  {
    id: 'malpractice-insurance',
    formName: 'Professional Liability/Malpractice Insurance',
    documentType: 'malpractice_insurance',
    description: 'Proof of current malpractice insurance coverage',
    required: false,
    hasExpiration: true,
    defaultExpirationMonths: 12
  },
  {
    id: 'cpr-certification',
    formName: 'CPR/First Aid Certification',
    documentType: 'certification',
    description: 'Current CPR and First Aid certification',
    required: false,
    hasExpiration: true,
    defaultExpirationMonths: 24
  },
  {
    id: 'benefits-enrollment',
    formName: 'Benefits Enrollment Form',
    documentType: 'employment_contract',
    description: 'Health insurance and benefits selection',
    required: false,
    hasExpiration: false
  }
];

const COLLECTION = 'hr_documents';

class HRDocumentService {
  private static instance: HRDocumentService;

  public static getInstance(): HRDocumentService {
    if (!HRDocumentService.instance) {
      HRDocumentService.instance = new HRDocumentService();
    }
    return HRDocumentService.instance;
  }

  // ── Form Templates (static) ───────────────────────────────────────────

  public getAllFormTemplates(): HRFormTemplate[] {
    return [...DEFAULT_HR_FORM_TEMPLATES];
  }

  public getFormTemplateById(id: string): HRFormTemplate | undefined {
    return DEFAULT_HR_FORM_TEMPLATES.find(t => t.id === id);
  }

  public getRequiredFormTemplates(): HRFormTemplate[] {
    return DEFAULT_HR_FORM_TEMPLATES.filter(t => t.required);
  }

  // ── Document CRUD (Firestore) ──────────────────────────────────────────

  async getAllDocuments(practiceOwnerId: string): Promise<HRDocument[]> {
    const q = query(
      collection(db, COLLECTION),
      where('practiceOwnerId', '==', practiceOwnerId),
      orderBy('assignedDate', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as HRDocument));
  }

  async getDocumentsByEmployeeId(practiceOwnerId: string, employeeId: string): Promise<HRDocument[]> {
    const q = query(
      collection(db, COLLECTION),
      where('practiceOwnerId', '==', practiceOwnerId),
      where('employeeId', '==', employeeId),
      orderBy('assignedDate', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as HRDocument));
  }

  async getDocumentById(documentId: string): Promise<HRDocument | null> {
    const snap = await getDoc(doc(db, COLLECTION, documentId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() } as HRDocument;
  }

  async assignDocumentToEmployee(
    employeeId: string,
    employeeName: string,
    formTemplateId: string,
    assignedBy: string,
    practiceOwnerId: string,
    priority: HRDocument['priority'] = 'medium',
    dueDate?: string
  ): Promise<string> {
    const template = this.getFormTemplateById(formTemplateId);
    if (!template) throw new Error('Form template not found: ' + formTemplateId);

    // Check if already assigned
    const existing = query(
      collection(db, COLLECTION),
      where('practiceOwnerId', '==', practiceOwnerId),
      where('employeeId', '==', employeeId),
      where('formName', '==', template.formName)
    );
    const existSnap = await getDocs(existing);

    if (!existSnap.empty) {
      // Update existing
      const existDoc = existSnap.docs[0];
      await updateDoc(doc(db, COLLECTION, existDoc.id), {
        assignedDate: new Date().toISOString(),
        assignedBy,
        priority,
        ...(dueDate ? { dueDate } : {}),
      });
      return existDoc.id;
    }

    // Create new
    const newDoc: Omit<HRDocument, 'id'> = {
      employeeId,
      employeeName,
      documentType: template.documentType,
      formName: template.formName,
      status: 'pending',
      assignedDate: new Date().toISOString(),
      assignedBy,
      practiceOwnerId,
      priority,
      ...(dueDate ? { dueDate } : {}),
    };

    const ref = await addDoc(collection(db, COLLECTION), newDoc);
    return ref.id;
  }

  async assignStandardFormsToNewEmployee(
    employeeId: string,
    employeeName: string,
    assignedBy: string,
    practiceOwnerId: string
  ): Promise<number> {
    const required = this.getRequiredFormTemplates();
    let count = 0;
    for (const template of required) {
      await this.assignDocumentToEmployee(employeeId, employeeName, template.id, assignedBy, practiceOwnerId, 'high');
      count++;
    }
    return count;
  }

  async markDocumentAsSubmitted(documentId: string, fileUrl: string, storagePath?: string): Promise<void> {
    const docSnap = await getDoc(doc(db, COLLECTION, documentId));
    if (!docSnap.exists()) return;

    const data = docSnap.data();
    const updates: any = {
      status: 'submitted',
      submittedDate: new Date().toISOString(),
      fileUrl,
      ...(storagePath ? { storagePath } : {}),
    };

    // Set expiration if applicable
    const template = DEFAULT_HR_FORM_TEMPLATES.find(t => t.formName === data.formName);
    if (template?.hasExpiration && template.defaultExpirationMonths) {
      const exp = new Date();
      exp.setMonth(exp.getMonth() + template.defaultExpirationMonths);
      updates.expirationDate = exp.toISOString();
    }

    await updateDoc(doc(db, COLLECTION, documentId), updates);
  }

  async approveDocument(documentId: string, approvedBy: string, notes?: string): Promise<void> {
    await updateDoc(doc(db, COLLECTION, documentId), {
      status: 'approved',
      approvedDate: new Date().toISOString(),
      approvedBy,
      ...(notes ? { notes } : {}),
    });
  }

  async rejectDocument(documentId: string, rejectedBy: string, notes: string): Promise<void> {
    await updateDoc(doc(db, COLLECTION, documentId), {
      status: 'rejected',
      approvedDate: new Date().toISOString(),
      approvedBy: rejectedBy,
      notes,
    });
  }

  async deleteDocument(documentId: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTION, documentId));
  }

  // ── Real-time listener ─────────────────────────────────────────────────

  subscribeToDocuments(practiceOwnerId: string, callback: (docs: HRDocument[]) => void): Unsubscribe {
    const q = query(
      collection(db, COLLECTION),
      where('practiceOwnerId', '==', practiceOwnerId),
      orderBy('assignedDate', 'desc')
    );
    return onSnapshot(q, (snap) => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() } as HRDocument));
      callback(docs);
    });
  }

  // ── Status helpers ─────────────────────────────────────────────────────

  getEmployeeDocumentStatus(documents: HRDocument[], employeeId: string) {
    const empDocs = documents.filter(d => d.employeeId === employeeId);
    const now = new Date();
    const pending = empDocs.filter(d => d.status === 'pending').length;
    const submitted = empDocs.filter(d => d.status === 'submitted').length;
    const approved = empDocs.filter(d => d.status === 'approved').length;
    const rejected = empDocs.filter(d => d.status === 'rejected').length;
    const expired = empDocs.filter(d => d.expirationDate && new Date(d.expirationDate) < now).length;

    const requiredTemplates = this.getRequiredFormTemplates();
    const requiredDocs = empDocs.filter(d => requiredTemplates.some(t => t.formName === d.formName));
    const requiredApproved = requiredDocs.filter(d => d.status === 'approved').length;

    return {
      total: empDocs.length,
      pending, submitted, approved, rejected, expired,
      allRequiredCompleted: requiredApproved === requiredTemplates.length
    };
  }

  // ── Live Editable Document Content ─────────────────────────────────────

  async updateLiveContent(documentId: string, htmlContent: string, editedBy: string): Promise<void> {
    const docRef = doc(db, COLLECTION, documentId);
    await updateDoc(docRef, {
      liveContent: htmlContent,
      lastEditedAt: new Date().toISOString(),
      lastEditedBy: editedBy
    });
  }

  async createLiveDocument(
    practiceOwnerId: string,
    title: string,
    createdBy: string,
    createdByName: string,
    initialContent?: string
  ): Promise<string> {
    const docRef = await addDoc(collection(db, COLLECTION), {
      practiceOwnerId,
      employeeId: createdBy,
      employeeName: createdByName,
      documentType: 'other',
      formName: title,
      status: 'approved',
      assignedDate: new Date().toISOString(),
      assignedBy: createdBy,
      priority: 'medium',
      liveContent: initialContent || '',
      lastEditedAt: new Date().toISOString(),
      lastEditedBy: createdBy
    });
    return docRef.id;
  }
}

export default HRDocumentService;
